import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import SelectSearchble from "../SelectSeachble/SelectSearchble";
import CoberturaPaciente from "./CoberturaPaciente";
import { minutosEntre, sumarMinutos, DURACIONES_CITA, ESTADOS_CUPO, iniciales } from "../../utils/agendaCitas";
import { tituloClinico } from "../../utils/textoClinico";
import "../../styles/ModalCita.css";

/* ============================================================================
   MODAL DE CITA · crear y editar

   Este modal vivía suelto dentro de `GestionCitas.js`, en un `row` de doce
   campos todos del mismo tamaño: no se veía qué era el cupo, qué era el
   paciente y qué era papeleo. Aquí sólo se ha reordenado y agrupado.

   LO QUE **NO** CAMBIA -- a propósito:

   · Las validaciones son las mismas cuatro que ya había (paciente, las dos
     horas, tipo de cita y al menos un procedimiento). Ni una más ni una
     menos, y siguen viviendo en `GuardarCita`, no aquí.
   · No se valida solape ni horario del médico. El backend NO lo valida: en la
     base hay 29 pares de citas solapadas y 2 con `hora_fin <= hora_inicio`.
     Pintar aquí un error inventaría una regla que el sistema no tiene.
   · El endpoint, su firma y su payload quedan intactos: se sigue enviando
     `hora_inicio` y `hora_fin`. La duración es sólo la forma de teclearlas.
   · Los permisos son los de antes (`citas.create` / `citas.edit` /
     `pacientes.create` / `citas.status_change`).

   Es un componente de PRESENTACIÓN: el estado y los manejadores siguen en
   `GestionCitas.js`. Recibe todo por props y no pide nada al servidor.
   ========================================================================== */

/** Suma los precios de una lista de procedimientos, tolerando precios vacíos. */
function sumarPrecios(lista) {
    return (Array.isArray(lista) ? lista : []).reduce((total, p) => total + (Number(p?.precio) || 0), 0);
}

export default function ModalCita({
    show,
    onCerrar,
    cita,
    setCita,
    // Paciente
    pacientes,
    pacienteElegido,
    onElegirPaciente,
    onCambiarPaciente,
    onNuevoPaciente,
    puedeCrearPaciente,
    // Procedimientos
    procedimientosOpciones,
    procedimientosElegidos,
    onElegirProcedimientos,
    procedimientosFacturados,
    cargandoProcedimientos,
    avisoProcedimientos,
    // Contexto del cupo
    doctor,
    especialidad,
    clinica,
    intervaloPorDefecto,
    // Catálogos
    tipoCitas,
    estadosCitas,
    // Facturación (mecanismo legacy, ver abajo)
    facturasPendientes,
    facturasElegidas,
    onElegirFacturas,
    // Acciones
    guardando,
    onGuardar,
    puedeGuardar,
    onCancelarCita,
    puedeCancelarCita,
}) {
    const esNueva = Number(cita?.idcita || 0) === 0;

    /* -- FASE 1: identidad de la cita -------------------------------------
       En edición, el número de cita es el dato con el que se habla por
       teléfono; antes no aparecía en ninguna parte. */
    const estado = ESTADOS_CUPO[Number(cita?.id_estado_cita)] ?? null;

    /* -- FASE 2: el cupo --------------------------------------------------
       Antes había dos `input[type=time]` independientes, así que nada impedía
       teclear un fin anterior al inicio. Ahora se elige INICIO + DURACIÓN y
       el fin se DERIVA con `sumarMinutos`, que ya existía para la cuadrícula.
       No se ha escrito aritmética de horas nueva.

       La duración por defecto NO es fija: sale de
       `clinica_especialidades.intervalo_tiempo`, igual que las franjas de la
       agenda (20 min en el 89 % de las citas reales, pero hay de 10 a 120). */
    const duracion = minutosEntre(cita?.hora_inicio, cita?.hora_fin);
    const duracionMostrada = duracion ?? (Number(intervaloPorDefecto) || 20);

    /* Una cita guardada puede traer una duración que no está en la lista (25,
       40...) o incluso incoherente. Se añade como opción en vez de
       reescribirle el dato al abrir: el modal no toca lo guardado hasta que
       alguien lo cambie a mano. */
    const opcionesDuracion = useMemo(() => {
        const todas = new Set(DURACIONES_CITA);
        if (duracion) todas.add(duracion);
        if (Number(intervaloPorDefecto) > 0) todas.add(Number(intervaloPorDefecto));
        return [...todas].sort((a, b) => a - b);
    }, [duracion, intervaloPorDefecto]);

    const cambiarInicio = (nuevoInicio) => {
        if (!nuevoInicio) {
            setCita({ ...cita, hora_inicio: "" });
            return;
        }
        setCita({
            ...cita,
            hora_inicio: nuevoInicio,
            hora_fin: sumarMinutos(nuevoInicio, duracionMostrada),
        });
    };

    const cambiarDuracion = (minutos) => {
        if (!cita?.hora_inicio) return;
        setCita({ ...cita, hora_fin: sumarMinutos(cita.hora_inicio, Number(minutos)) });
    };

    /* -- FASE 6: "Guardar cambios" apagado hasta que algo cambie ----------
       Se compara contra una foto tomada al ABRIR. Sin esto, en edición el
       botón invita a guardar una cita idéntica. */
    /* La foto es ESTADO, no un ref: un ref se puede actualizar sin volver a
       pintar, y entonces el botón se queda con la comparación vieja. */
    const [fotoInicial, setFotoInicial] = useState(null);
    const firma = JSON.stringify({
        cita,
        procedimientos: (procedimientosElegidos || []).map((p) => p?.value).sort(),
        facturas: (facturasElegidas || []).map((f) => f?.value).sort(),
    });

    const cargandoPrevio = useRef(false);

    useEffect(() => {
        if (!show) {
            setFotoInicial(null);
            cargandoPrevio.current = false;
            return;
        }
        /* La foto se toma al abrir Y otra vez en cuanto termina la precarga de
           procedimientos, que llega DESPUÉS. Sin lo segundo, en edición los
           procedimientos que el sistema precarga solos contaban como un cambio
           del usuario y el botón se encendía sin que nadie hubiera tocado
           nada. `setCargandoProcedimientos(false)` va en el mismo bloque que
           las selecciones, así que al correr esto ya están puestas. */
        if (fotoInicial === null || (cargandoPrevio.current && !cargandoProcedimientos)) {
            // Poner el mismo valor no vuelve a pintar: React lo descarta.
            setFotoInicial(firma);
        }
        cargandoPrevio.current = cargandoProcedimientos;
        // La foto es el punto de partida, no un espejo de cada tecla.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, cargandoProcedimientos]);

    const hayCambios = fotoInicial !== null && fotoInicial !== firma;

    const totalReferencial = sumarPrecios(procedimientosElegidos) + sumarPrecios(procedimientosFacturados);

    /* Se avisa a quien nos abrió si hay algo sin guardar; la confirmación vive
       allí, donde ya está Swal, no en un componente de presentación. */
    const cerrar = () => onCerrar(hayCambios);

    /* El nombre viene anidado: la fila es de `clinica_especialidades` y el
       nombre vive en su relación `especialidades`. */
    const nombreEspecialidad =
        especialidad?.especialidades?.especialidad || especialidad?.especialidad || especialidad?.nombre;
    const subtitulo = [nombreEspecialidad, clinica]
        .filter(Boolean)
        .map((t) => tituloClinico(t))
        .join(" · ");

    return (
        <Modal show={show} onHide={cerrar} scrollable dialogClassName="mcita" backdrop="static" keyboard>
            <Modal.Header closeButton>
                <div className="mcita__titulo">
                    <div className="mcita__titulo-linea">
                        <h5 className="mcita__nombre">{esNueva ? "Nueva cita" : `Cita #${cita.idcita}`}</h5>
                        {!esNueva && estado && (
                            <span className={`mcita__estado mcita__estado--${estado.clave}`}>
                                <span className="mcita__punto" aria-hidden="true" />
                                {estado.etiqueta}
                            </span>
                        )}
                    </div>
                    {subtitulo && <p className="mcita__subtitulo">{subtitulo}</p>}
                </div>
            </Modal.Header>

            <Modal.Body>
                {/* ============ EL CUPO ============================
                    Médico, día y hora: lo que define el hueco en la agenda.
                    El médico y la fecha son de sólo lectura porque el cupo se
                    elige haciendo clic en la cuadrícula, no aquí -- eso ya era
                    así, sólo se explica mejor. */}
                <section className="mcita__bloque">
                    <h6 className="mcita__bloque-titulo">El cupo</h6>

                    <div className="mcita__medico">
                        <span className="mcita__avatar" aria-hidden="true">
                            {iniciales(doctor?.nombre || "")}
                        </span>
                        <span className="mcita__medico-datos">
                            <span className="mcita__campo-etiqueta">Médico</span>
                            <strong className="mcita__medico-nombre">
                                {doctor?.nombre ? tituloClinico(doctor.nombre) : "Sin asignar"}
                            </strong>
                        </span>
                    </div>

                    <div className="mcita__rejilla">
                        <label className="mcita__campo">
                            <span className="mcita__campo-etiqueta">Fecha</span>
                            <input
                                type="date"
                                className="mcita__control"
                                value={cita?.fecha_inicio || ""}
                                readOnly
                            />
                        </label>

                        <label className="mcita__campo">
                            <span className="mcita__campo-etiqueta">Inicio</span>
                            <input
                                type="time"
                                className="mcita__control"
                                value={cita?.hora_inicio || ""}
                                onChange={(e) => cambiarInicio(e.target.value)}
                            />
                        </label>

                        <label className="mcita__campo">
                            <span className="mcita__campo-etiqueta">Duración</span>
                            <select
                                className="mcita__control"
                                value={duracionMostrada}
                                onChange={(e) => cambiarDuracion(e.target.value)}
                                disabled={!cita?.hora_inicio}
                            >
                                {opcionesDuracion.map((m) => (
                                    <option key={m} value={m}>
                                        {m} min
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {/* El fin ya no se teclea: se dice. */}
                    {cita?.hora_inicio && cita?.hora_fin && (
                        <p className="mcita__derivado" data-testid="cita-termina">
                            Termina a las <strong>{cita.hora_fin}</strong>
                        </p>
                    )}

                    {/* Las 2 citas reales con el fin antes del inicio: se
                        muestra el dato tal cual y se avisa, sin corregirlo por
                        nuestra cuenta ni bloquear el guardado (el backend no
                        lo prohíbe). */}
                    {cita?.hora_inicio && cita?.hora_fin && duracion === null && (
                        <p className="mcita__ojo">
                            Esta cita tiene la hora de fin ({cita.hora_fin}) igual o anterior a la de inicio. Elige una
                            duración para corregirla.
                        </p>
                    )}
                </section>

                {/* ============ EL PACIENTE ========================= */}
                <section className="mcita__bloque">
                    <h6 className="mcita__bloque-titulo">El paciente</h6>

                    {/* En EDICIÓN el paciente no se cambia de un descuido: se
                        muestra la ficha y hay que pedirlo. Cambiarlo sigue
                        permitido -- el sistema lo permite y no hay regla que
                        lo impida -- sólo deja de ser un clic accidental. */}
                    {!esNueva && pacienteElegido ? (
                        <div className="mcita__ficha">
                            <div className="mcita__ficha-datos">
                                <strong className="mcita__ficha-nombre">
                                    {tituloClinico(pacienteElegido.label || pacienteElegido.nombres_completos || "")}
                                </strong>
                                <CoberturaPaciente paciente={pacienteElegido} />
                            </div>
                            <button type="button" className="mcita__enlace" onClick={onCambiarPaciente}>
                                Cambiar
                            </button>
                        </div>
                    ) : (
                        <div className="mcita__campo">
                            <span className="mcita__campo-etiqueta">
                                Paciente <span className="mcita__obligatorio">*</span>
                            </span>
                            <div className="mcita__con-boton">
                                <SelectSearchble
                                    listCompany={pacientes}
                                    handleChange={onElegirPaciente}
                                    selectedOption={pacienteElegido}
                                    placeholder="Buscar paciente"
                                    styles={{ width: "100%" }}
                                />
                                {puedeCrearPaciente && (
                                    <button
                                        type="button"
                                        className="mcita__boton-nuevo"
                                        onClick={onNuevoPaciente}
                                        title="Crear un paciente nuevo"
                                    >
                                        <i className="fas fa-plus" aria-hidden="true"></i>
                                        <span className="mcita__solo-lector">Crear paciente</span>
                                    </button>
                                )}
                            </div>

                            {/* CITAS-COBERTURA: a qué pertenece el paciente y
                                qué plan tiene. El objeto del selector ya trae
                                `tipo_seguros`, `seguros` y `plan`: esto no pide
                                nada, sólo pinta lo que ya venía sin usarse. */}
                            <CoberturaPaciente paciente={pacienteElegido} />
                        </div>
                    )}

                    {/* -- Procedimientos ------------------------------------
                        Va pegado al paciente porque el catálogo depende de su
                        cobertura (CITAS-PROCEDIMIENTOS-03). Lleva asterisco
                        porque es obligatorio de verdad: el botón de guardar se
                        apaga sin ninguno. */}
                    <div className="mcita__campo">
                        <span className="mcita__campo-etiqueta">
                            Procedimientos <span className="mcita__obligatorio">*</span>
                        </span>
                        <SelectSearchble
                            listCompany={procedimientosOpciones}
                            handleChange={onElegirProcedimientos}
                            selectedOption={procedimientosElegidos}
                            placeholder={
                                !cita?.idpaciente
                                    ? "Elige primero un paciente"
                                    : cargandoProcedimientos
                                    ? "Cargando procedimientos..."
                                    : procedimientosOpciones?.length === 0
                                    ? "Este paciente no tiene procedimientos con tarifario activo"
                                    : "Buscar y elegir procedimientos"
                            }
                            isMulti={true}
                            isDisabled={!cita?.idpaciente || cargandoProcedimientos}
                        />

                        {avisoProcedimientos && <p className="mcita__error">{avisoProcedimientos}</p>}

                        {/* Una sola línea de ayuda en vez de tres frases. El
                            precio es referencial: el real lo pone el servidor
                            al guardar, desde el tarifario. */}
                        <p className="mcita__ayuda">
                            Los precios son referenciales; el precio real se toma del tarifario al guardar.
                        </p>

                        {totalReferencial > 0 && (
                            <p className="mcita__total" data-testid="cita-referencial">
                                Referencial <strong>${totalReferencial.toFixed(2)}</strong>
                            </p>
                        )}

                        {procedimientosFacturados?.length > 0 && (
                            <div className="mcita__facturados">
                                <span className="mcita__campo-etiqueta">Ya facturados, no editables</span>
                                <ul className="mcita__lista">
                                    {procedimientosFacturados.map((p) => (
                                        <li key={p.idprocedimiento_cita_paciente} className="mcita__lista-fila">
                                            <span>
                                                <i className="fas fa-lock" aria-hidden="true"></i> {p.label}
                                            </span>
                                            <span className="mcita__lista-precio">
                                                ${Number(p.precio).toFixed(2)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>

                {/* ============ CLASIFICACIÓN ======================= */}
                <section className="mcita__bloque">
                    <h6 className="mcita__bloque-titulo">Clasificación</h6>

                    <div className="mcita__rejilla mcita__rejilla--dos">
                        <label className="mcita__campo">
                            <span className="mcita__campo-etiqueta">
                                Tipo de cita <span className="mcita__obligatorio">*</span>
                            </span>
                            <select
                                className="mcita__control"
                                value={cita?.idtipo_cita || ""}
                                onChange={(e) => setCita({ ...cita, idtipo_cita: parseInt(e.target.value, 10) })}
                            >
                                <option value="">Elige una opción</option>
                                {(tipoCitas || []).map((tipo) => (
                                    <option key={tipo.idtipo_cita} value={tipo.idtipo_cita}>
                                        {tipo.tipo}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="mcita__campo">
                            <span className="mcita__campo-etiqueta">Estado</span>
                            <div className="mcita__con-punto">
                                {estado && (
                                    <span
                                        className={`mcita__punto mcita__punto--${estado.clave}`}
                                        aria-hidden="true"
                                    />
                                )}
                                <select
                                    className="mcita__control"
                                    value={cita?.id_estado_cita || ""}
                                    onChange={(e) =>
                                        setCita({ ...cita, id_estado_cita: parseInt(e.target.value, 10) })
                                    }
                                >
                                    <option value="">Elige una opción</option>
                                    {(estadosCitas || []).map((e) => (
                                        <option key={e.idestado_cita} value={e.idestado_cita}>
                                            {e.estado}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </label>
                    </div>

                    <label className="mcita__campo">
                        <span className="mcita__campo-etiqueta">Motivo</span>
                        <textarea
                            className="mcita__control mcita__area"
                            rows={2}
                            value={cita?.motivo || ""}
                            onChange={(e) => setCita({ ...cita, motivo: e.target.value })}
                        />
                    </label>
                </section>

                {/* ============ TRAZABILIDAD (plegado) ==============
                    Esto NO cobra ni añade procedimientos: hace un
                    `UPDATE procedimiento_cita_paciente SET idcita = <esta>`
                    sobre lo que el paciente ya tiene facturado del MISMO DÍA.
                    Es trazabilidad hacia atrás -- el propio código lo llama
                    mecanismo legacy -- y por eso va plegado y al final, no
                    entre los campos del cupo como si fuese parte de crear la
                    cita. */}
                <details className="mcita__plegado">
                    <summary className="mcita__plegado-titulo">
                        Vincular procedimientos ya facturados
                        {facturasPendientes?.length > 0 && (
                            <span className="mcita__contador">{facturasPendientes.length}</span>
                        )}
                    </summary>
                    <div className="mcita__plegado-cuerpo">
                        <p className="mcita__ayuda">
                            Marca como pertenecientes a esta cita los procedimientos ya facturados del paciente del
                            mismo día. No cobra nada ni añade procedimientos.
                        </p>
                        {facturasPendientes?.length > 0 ? (
                            <SelectSearchble
                                listCompany={facturasPendientes}
                                handleChange={onElegirFacturas}
                                selectedOption={facturasElegidas}
                                placeholder="Elige una o varias facturas"
                                isMulti={true}
                            />
                        ) : (
                            <p className="mcita__vacio">Este paciente no tiene facturas pendientes.</p>
                        )}
                    </div>
                </details>
            </Modal.Body>

            {/* ============ FASE 6: el pie, fijo ====================
                Antes los botones iban DENTRO del cuerpo, así que el modal los
                cortaba: 19 px con la cita vacía y 58 px con dos
                procedimientos. Con `scrollable` el cuerpo desplaza y el pie
                se queda. */}
            <Modal.Footer className="mcita__pie">
                {/* Cancelar la CITA no es cerrar el modal: son cosas
                    distintas y antes se parecían demasiado. Va a la
                    izquierda, separado, en texto rojo y no como botón. */}
                <div className="mcita__pie-izquierda">
                    {!esNueva && puedeCancelarCita && (
                        <button
                            type="button"
                            className="mcita__cancelar-cita"
                            onClick={onCancelarCita}
                            disabled={guardando}
                        >
                            Cancelar cita
                        </button>
                    )}
                </div>

                <div className="mcita__pie-derecha">
                    <button type="button" className="mcita__boton" onClick={cerrar} disabled={guardando}>
                        Cerrar
                    </button>
                    <button
                        type="button"
                        className="mcita__boton mcita__boton--principal"
                        onClick={onGuardar}
                        disabled={
                            guardando ||
                            !procedimientosElegidos?.length ||
                            !puedeGuardar ||
                            /* En edición, nada que guardar = nada que pulsar.
                               En creación siempre hay cambios, así que no
                               estorba. */
                            (!esNueva && !hayCambios)
                        }
                    >
                        {guardando ? "Guardando..." : esNueva ? "Crear cita" : "Guardar cambios"}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
